/**
 * Almikia Epic EO (TASK-100), DOMAIN RULES EO (e): plain, framework-free
 * functions backing the "Nuestra cultura" editor -- length caps, the
 * unchanged-save guard and the Markdown formatting-help actions (bold,
 * italic, bullet list, link). Kept free of React/react-admin coupling so
 * they are unit-testable on their own (see `cultureDocumentForm.test.ts`),
 * mirroring `incentiveProgramForm.ts`'s split between plain logic and
 * components.
 *
 * The length caps mirror DATA CONTRACT EO (3) exactly (title <= 120 chars,
 * body <= 20,000 chars, both trimmed non-blank) -- enforced here client-side
 * in addition to the server/RLS check (`culture_document_versions`' CHECK
 * constraints, TASK-096), per the ticket's own instruction.
 */

export const CULTURE_TITLE_MAX_LENGTH = 120;
export const CULTURE_BODY_MAX_LENGTH = 20000;

/** The two fields a `culture_document_versions` row's content is made of. */
export interface CultureDocumentContent {
  title: string;
  body_markdown: string;
}

/**
 * A blank title, or one over the length cap, is rejected -- returns an i18n
 * key for the caller to translate, or `undefined` when valid.
 */
export function validateCultureTitle(value: string): string | undefined {
  if (!value.trim()) {
    return "crm.culture.editor.error.title_required";
  }
  if (value.trim().length > CULTURE_TITLE_MAX_LENGTH) {
    return "crm.culture.editor.error.title_too_long";
  }
  return undefined;
}

/**
 * A blank body, or one over the length cap, is rejected -- returns an i18n
 * key for the caller to translate, or `undefined` when valid.
 */
export function validateCultureBody(value: string): string | undefined {
  if (!value.trim()) {
    return "crm.culture.editor.error.body_required";
  }
  if (value.trim().length > CULTURE_BODY_MAX_LENGTH) {
    return "crm.culture.editor.error.body_too_long";
  }
  return undefined;
}

/**
 * The "No hay cambios que guardar" guard (DOMAIN RULES EO (e)): a save whose
 * title and body are both unchanged (after trimming, so trailing whitespace
 * alone never counts as a change) is blocked so no empty version is created.
 */
export function hasCultureDocumentChanges(
  original: CultureDocumentContent,
  next: CultureDocumentContent,
): boolean {
  return (
    original.title.trim() !== next.title.trim() ||
    original.body_markdown.trim() !== next.body_markdown.trim()
  );
}

/**
 * "Nuevo documento": a lowercase `[a-z0-9-]+` slug derived from the title
 * (DATA CONTRACT EO (2)'s format check), de-duplicated against
 * `existingSlugs` by appending `-2`, `-3`, etc. Falls back to "documento"
 * when the title has no ASCII letters/digits at all (e.g. only punctuation),
 * so a slug is always produced.
 */
export function slugifyCultureTitle(
  title: string,
  existingSlugs: string[] = [],
): string {
  const base =
    title
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "documento";

  if (!existingSlugs.includes(base)) {
    return base;
  }
  let suffix = 2;
  while (existingSlugs.includes(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}

/** A textarea's current value plus its selection, and the same after an edit. */
export interface TextSelection {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/** Wraps the selected text (or a placeholder, when nothing is selected) in `prefix`/`suffix`. */
function wrapSelection(
  selection: TextSelection,
  prefix: string,
  suffix: string,
  placeholder: string,
): TextSelection {
  const { value, selectionStart, selectionEnd } = selection;
  const selectedText = value.slice(selectionStart, selectionEnd) || placeholder;
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const nextValue = `${before}${prefix}${selectedText}${suffix}${after}`;
  const newSelectionStart = before.length + prefix.length;
  const newSelectionEnd = newSelectionStart + selectedText.length;
  return {
    value: nextValue,
    selectionStart: newSelectionStart,
    selectionEnd: newSelectionEnd,
  };
}

/** Formatting-help "Negritas" action: wraps the selection in `**`. */
export function applyBoldFormatting(selection: TextSelection): TextSelection {
  return wrapSelection(selection, "**", "**", "texto en negritas");
}

/** Formatting-help "Cursivas" action: wraps the selection in `_`. */
export function applyItalicFormatting(selection: TextSelection): TextSelection {
  return wrapSelection(selection, "_", "_", "texto en cursivas");
}

/**
 * Formatting-help "Lista" action: prefixes every line touched by the
 * selection with "- " (a Markdown bullet), skipping a line that already has
 * one. Selects the whole modified block afterwards.
 */
export function applyBulletFormatting(selection: TextSelection): TextSelection {
  const { value, selectionStart, selectionEnd } = selection;
  const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
  const nextNewline = value.indexOf("\n", selectionEnd);
  const lineEnd = nextNewline === -1 ? value.length : nextNewline;

  const block = value.slice(lineStart, lineEnd);
  const lines = block.length > 0 ? block.split("\n") : [""];
  const prefixed = lines
    .map((line) => (line.startsWith("- ") ? line : `- ${line}`))
    .join("\n");

  const nextValue = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
  return {
    value: nextValue,
    selectionStart: lineStart,
    selectionEnd: lineStart + prefixed.length,
  };
}

/**
 * Formatting-help "Enlace" action: wraps the selection (or a placeholder) as
 * link text and appends a placeholder URL, with the URL itself pre-selected
 * so typing immediately replaces it.
 */
export function applyLinkFormatting(selection: TextSelection): TextSelection {
  const { value, selectionStart, selectionEnd } = selection;
  const linkText =
    value.slice(selectionStart, selectionEnd) || "texto del enlace";
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const url = "https://";
  const inserted = `[${linkText}](${url})`;
  const nextValue = `${before}${inserted}${after}`;
  const urlStart = before.length + linkText.length + 3; // "[" + linkText + "]("
  return {
    value: nextValue,
    selectionStart: urlStart,
    selectionEnd: urlStart + url.length,
  };
}
